import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, query, where, getDocs, deleteDoc, doc, getDoc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import './AdminPanel.css';

export default function AdminPanel() {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [villages, setVillages] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const navigate = useNavigate();

  // Helper function to safely format dates
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    try {
      // Handle Firestore Timestamp
      if (dateValue.toDate && typeof dateValue.toDate === 'function') {
        return dateValue.toDate().toLocaleDateString();
      }
      // Handle JavaScript Date object
      if (dateValue instanceof Date) {
        return dateValue.toLocaleDateString();
      }
      // Handle timestamp number
      if (typeof dateValue === 'number') {
        return new Date(dateValue).toLocaleDateString();
      }
      // Handle ISO string
      if (typeof dateValue === 'string') {
        return new Date(dateValue).toLocaleDateString();
      }
      // Handle Firestore Timestamp-like object with _seconds
      if (dateValue._seconds && typeof dateValue._seconds === 'number') {
        return new Date(dateValue._seconds * 1000).toLocaleDateString();
      }
      
      return 'N/A';
    } catch (error) {
      console.warn('Error formatting date:', error);
      return 'N/A';
    }
  };

  // Check authentication
  useEffect(() => {
    const adminAuth = localStorage.getItem('adminAuth');
    if (!adminAuth) {
      navigate('/admin-login');
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    try {
      // Fetch villages
      const villagesQuery = query(collection(db, "villages"));
      const villagesSnapshot = await getDocs(villagesQuery);
      const villageList = villagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch accounts
      const accountsQuery = query(collection(db, "accounts"));
      const accountsSnapshot = await getDocs(accountsQuery);
      const accountList = accountsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate account counts per village
      const villageWithAccounts = villageList.map(village => {
        const accountCount = accountList.filter(acc => acc.villageId === village.id).length;
        return { ...village, accountCount };
      });

      setVillages(villageWithAccounts);
      setAccounts(accountList);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const createVillage = async (e) => {
    e.preventDefault();
    if (!name.trim() || !code.trim()) {
      setMessage('Please fill all fields');
      return;
    }

    try {
      // Check if code exists
      const q = query(collection(db, "villages"), where("code", "==", code.trim()));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        setMessage('Village Code already exists!');
        return;
      }

      await addDoc(collection(db, "villages"), {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        createdAt: new Date()
      });

      setMessage('Village Created Successfully!');
      setName('');
      setCode('');
      fetchAllData(); // Refresh all data
    } catch (error) {
      console.error("Error:", error);
      setMessage('Error creating village');
    }
  };

  const deleteVillage = async (villageId) => {
    if (!confirm('Are you sure you want to delete this village? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, "villages", villageId));
      setMessage('Village deleted successfully!');
      fetchAllData(); // Refresh all data
    } catch (error) {
      console.error("Error deleting village:", error);
      setMessage('Error deleting village');
    }
  };

  const logout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/admin-login');
  };

  // Filter villages based on search and filter criteria
  const filteredVillages = villages.filter(village => {
    const matchesSearch = village.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         village.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterBy === 'all' || village.accountCount > 0;
    return matchesSearch && matchesFilter;
  });

  // Calculate statistics
  const totalVillages = villages.length;
  const totalAccounts = accounts.length;
  const villagesWithAccounts = villages.filter(v => v.accountCount > 0).length;
  const villagesWithoutAccounts = totalVillages - villagesWithAccounts;

  return (
    <div className="admin-panel-container">
      <div className="admin-header">
        <div className="admin-title">
          <h1>Admin Dashboard</h1>
          <p>Manage Villages and Monitor Reports</p>
        </div>
        <button onClick={logout} className="logout-btn">
          Logout
        </button>
      </div>

      {/* Statistics Overview */}
      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className="stat-content">
            <h3>Total Villages</h3>
            <p className="stat-number">{totalVillages}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className="stat-content">
            <h3>Total Accounts</h3>
            <p className="stat-number">{totalAccounts}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className="stat-content">
            <h3>Villages with Accounts</h3>
            <p className="stat-number">{villagesWithAccounts}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className="stat-content">
            <h3>Villages without Accounts</h3>
            <p className="stat-number">{villagesWithoutAccounts}</p>
          </div>
        </div>
      </div>

      <div className="admin-content">
        <div className="create-village-card">
          <h2>Create New Village</h2>
          {message && (
            <div className={`message ${message.includes('Successfully') || message.includes('deleted') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}
          <form onSubmit={createVillage} className="village-form">
            <div className="form-row">
              <div className="form-group">
                <label>Village Name</label>
                <input
                  type="text"
                  placeholder="Enter village name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Village Code</label>
                <input
                  type="text"
                  placeholder="e.g., VIL001"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="form-input"
                  maxLength="10"
                />
              </div>
            </div>
            <button type="submit" className="create-btn">
              + Create Village
            </button>
          </form>
        </div>

        <div className="villages-list-card">
          <h2>Existing Villages</h2>
          <div className="search-filter">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search villages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </div>
            <div className="filter-options">
              <label>
                <input
                  type="radio"
                  name="filter"
                  value="all"
                  checked={filterBy === 'all'}
                  onChange={() => setFilterBy('all')}
                />
                All Villages
              </label>
              <label>
                <input
                  type="radio"
                  name="filter"
                  value="with-accounts"
                  checked={filterBy === 'with-accounts'}
                  onChange={() => setFilterBy('with-accounts')}
                />
                With Accounts
              </label>
              <label>
                <input
                  type="radio"
                  name="filter"
                  value="without-accounts"
                  checked={filterBy === 'without-accounts'}
                  onChange={() => setFilterBy('without-accounts')}
                />
                Without Accounts
              </label>
            </div>
          </div>

          {loading ? (
            <div className="loading">Loading villages...</div>
          ) : filteredVillages.length === 0 ? (
            <div className="no-data">No villages found matching your criteria.</div>
          ) : (
            <div className="villages-grid">
              {filteredVillages.map((village) => (
                <div key={village.id} className="village-card">
                  <div className="village-info">
                    <h3>{village.name}</h3>
                    <p className="village-code">Code: <strong>{village.code}</strong></p>
                    <p className="village-date">
                      Created: {formatDate(village.createdAt)}
                    </p>
                    <div className="account-stats">
                      <span className="account-count">{village.accountCount} Accounts</span>
                      <div className="account-progress" style={{ width: `${Math.min(village.accountCount * 10, 100)}%` }}></div>
                    </div>
                  </div>
                  <div className="village-actions">
                    <button
                      onClick={() => navigate(`/village-login`)}
                      className="view-btn"
                    >
                      View Dashboard
                    </button>
                    <button
                      onClick={() => deleteVillage(village.id)}
                      className="delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
